/**
 * Phase 4 — service-worker registration.
 *
 * Called once from index.js after the React tree mounts. We register
 * /service-worker.js (deployed to the build root) and wire up a
 * `controllerchange` listener so a freshly activated SW immediately
 * takes effect on the page (the SW uses skipWaiting + claim, but the
 * page also needs to know to swap its controlling worker).
 *
 * Disabled in development by default — CRA's dev server has hot module
 * reload and a SW that caches stale bundles between code edits is
 * miserable to debug. Override via REACT_APP_ENABLE_SW=true if you
 * specifically want to test the SW locally.
 */

const SW_PATH = '/service-worker.js';
// Poll for SW updates every 10 minutes. Cheap: just a HEAD-ish fetch
// against /service-worker.js — the browser's HTTP cache + the file's
// ETag will short-circuit unless something actually changed.
const UPDATE_POLL_INTERVAL_MS = 10 * 60 * 1000;

function _shouldRegister() {
  if (typeof navigator === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (process.env.NODE_ENV !== 'production') {
    return process.env.REACT_APP_ENABLE_SW === 'true';
  }
  return true;
}

// ─── Update-available event source ─────────────────────────────────────
// The "Refresh now" banner subscribes here. Whenever a registration's
// `waiting` slot becomes non-null OR the page's controller is older
// than the registration's active worker, we know a new bundle is sitting
// ready to take over — emit so the UI can prompt.
const _updateListeners = new Set();
let _updateAvailable = false;

function _setUpdateAvailable(reg) {
  if (_updateAvailable) return;
  _updateAvailable = true;
  for (const fn of _updateListeners) {
    try { fn({ available: true, registration: reg }); } catch (_) { /* swallow */ }
  }
}

/** Subscribe to "a new app version is ready" notifications. Returns an
 *  unsubscribe function. */
export function onUpdateAvailable(fn) {
  _updateListeners.add(fn);
  // Fire immediately if we already know a version is waiting.
  if (_updateAvailable) {
    try { fn({ available: true }); } catch (_) { /* swallow */ }
  }
  return () => _updateListeners.delete(fn);
}

/** Tell the waiting SW to take over NOW. The controllerchange listener
 *  in registerServiceWorker() then reloads the page once. */
export async function applyUpdateAndReload() {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg && reg.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Some browsers don't honour SKIP_WAITING via postMessage if the
      // SW didn't listen for it. We do listen (added below), but the
      // reload below is the belt-and-braces fallback.
    }
  } catch (_) { /* ignore */ }
  // Hard reload (bypassing the HTTP cache) so the new bundle's
  // immutable hashed assets are fetched fresh.
  window.location.reload();
}

function _watchRegistration(reg) {
  // A new SW is being installed RIGHT NOW.
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      // 'installed' + an existing controller means an UPDATE happened
      // (vs a first install where there was no previous controller).
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        _setUpdateAvailable(reg);
      }
    });
  });
  // A SW was already waiting when we attached — fire now.
  if (reg.waiting && navigator.serviceWorker.controller) {
    _setUpdateAvailable(reg);
  }
}

/**
 * Public entry. Safe to call multiple times — registration is
 * idempotent at the browser layer.
 */
export async function registerServiceWorker() {
  if (!_shouldRegister()) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    // When the SW we just registered ships a new version, we want the
    // page to reload so it picks up the new bundle. Only fire AFTER
    // the first activation — the first one runs without a reload by
    // construction (page just loaded).
    let firstActivation = true;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (firstActivation) { firstActivation = false; return; }
      // Reload once; guard against loops by checking a sessionStorage flag.
      try {
        const k = 'mojo-sw-reloaded';
        if (sessionStorage.getItem(k)) return;
        sessionStorage.setItem(k, '1');
        // eslint-disable-next-line no-console
        console.info('mojo-sw: new version active — reloading once');
        window.location.reload();
      } catch (_) { /* ignore */ }
    });
    // Wire the "update is waiting" detection + periodic poll.
    _watchRegistration(reg);
    // Periodic update check. registration.update() asks the browser to
    // re-fetch the SW script; the browser's HTTP cache short-circuits
    // unless something genuinely changed. When a new version IS found,
    // the 'updatefound' event fires + _watchRegistration emits.
    setInterval(() => {
      try { reg.update(); } catch (_) { /* swallow — next interval retries */ }
    }, UPDATE_POLL_INTERVAL_MS);
    // Also check on tab visibility change — if the user comes back
    // after a few hours we want them on the latest version before they
    // submit anything.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        try { reg.update(); } catch (_) { /* swallow */ }
      }
    });
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('mojo-sw: registration failed', e);
    return null;
  }
}

/** Ask the SW to dump its API response cache and wait for the ack so
 *  callers can safely refetch immediately afterwards. Without the ack,
 *  a refreshData() called right after clearApiCache() would race the
 *  SW's caches.delete() and still pick up the stale entry — the exact
 *  shape of the location-update "didn't save" bug. */
export async function clearApiCache() {
  if (!('serviceWorker' in navigator)) return;
  let reg;
  try {
    reg = await navigator.serviceWorker.ready;
  } catch (_) {
    return;
  }
  if (!reg.active) return;
  await new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = setTimeout(resolve, 600); // belt-and-braces
    channel.port1.onmessage = () => { clearTimeout(timeout); resolve(); };
    try {
      reg.active.postMessage({ type: 'MOJO_CLEAR_API_CACHE' }, [channel.port2]);
    } catch (_) {
      clearTimeout(timeout);
      resolve();
    }
  });
}
